import { Reveal } from "../landing/Reveal";
import { HighlightedQuote } from "./HighlightedQuote";
import { BOOKING_URL } from "../landing/booking";
import {
  TS_BADGES,
  TS_CAMPAIGN,
  TS_CASE_STUDY_ID,
  TS_CUSTOMER,
  TS_HEADLINE,
  TS_HOMEPAGE,
  TS_INSIDE,
  TS_LOGO,
  TS_PHOTO,
  TS_QUOTES,
  TS_QUOTES_NOTE,
  TS_STATS,
} from "../../use-cases/case-studies/teachingStrategies";

/**
 * The full Teaching Strategies case study, on the Education page. The home
 * page's CaseStudyTeaser links here (#teaching-strategies). Deliberately no
 * link to the live narrative.
 */
export const TeachingStrategiesCaseStudy = () => (
  <section
    id={TS_CASE_STUDY_ID}
    tabIndex={-1}
    aria-labelledby="ts-case-study-heading"
    className="scroll-mt-(--site-header-h,4rem) focus:outline-none bg-fwd-navy text-white py-24 md:py-32 px-6 md:px-16 lg:px-24 relative overflow-hidden"
  >
    <div className="max-w-6xl mx-auto">
      <Reveal>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mb-12 md:mb-16">
          <span className="bg-fwd-yellow text-fwd-ink text-[0.7rem] font-bold tracking-[0.18em] uppercase rounded-full px-3 py-1.5">
            Case study
          </span>
          <img
            src={TS_LOGO.src}
            alt={TS_LOGO.alt}
            width={TS_LOGO.width}
            height={TS_LOGO.height}
            className="w-40 md:w-52 max-w-full h-auto brightness-0 invert"
          />
          <span className="text-white/50 text-xs font-bold tracking-[0.2em] uppercase">
            {TS_CAMPAIGN}
          </span>
        </div>
      </Reveal>

      <div className="grid gap-14 lg:grid-cols-12 lg:gap-16 items-start">
        {/* Photo with the campaign's real recognition badges */}
        <Reveal className="lg:col-span-5">
          <figure className="relative mx-auto max-w-md lg:max-w-none pb-10">
            <img
              src={TS_PHOTO.src}
              alt={TS_PHOTO.alt}
              width={TS_PHOTO.width}
              height={TS_PHOTO.height}
              loading="lazy"
              decoding="async"
              className="w-full h-auto rounded-2xl object-cover aspect-[4/5] shadow-[0_30px_80px_rgba(0,0,0,0.45)]"
            />
            <figcaption className="absolute left-4 bottom-0 flex -space-x-4 sm:-space-x-5">
              {TS_BADGES.map((b) => (
                <img
                  key={b.src}
                  src={b.src}
                  alt={`${b.label} badge`}
                  width={88}
                  height={88}
                  loading="lazy"
                  className="w-[min(4rem,17vw)] h-[min(4rem,17vw)] sm:w-20 sm:h-20 md:w-[88px] md:h-[88px] rounded-full bg-white shadow-[0_8px_24px_rgba(0,0,0,0.3)] ring-4 ring-fwd-navy"
                />
              ))}
            </figcaption>
          </figure>
        </Reveal>

        <div className="lg:col-span-7">
          <Reveal>
            <h2
              id="ts-case-study-heading"
              className="font-display text-[clamp(2.2rem,4vw,3.5rem)] font-normal leading-tight mb-5"
            >
              {TS_HEADLINE}
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-xl">
              At the end of the school year,{" "}
              <a
                href={TS_HOMEPAGE}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white underline decoration-fwd-yellow decoration-2 underline-offset-4 hover:decoration-white transition-colors"
              >
                {TS_CUSTOMER}
              </a>{" "}
              sent each early-childhood program it serves a narrative of that
              program&rsquo;s year. Each one covered:
            </p>
            <ul className="grid gap-2.5 mb-12 text-white/85">
              {TS_INSIDE.map((item) => (
                <li key={item} className="flex gap-3">
                  <span
                    aria-hidden="true"
                    className="mt-2 h-2 w-2 shrink-0 rounded-full bg-fwd-yellow"
                  />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.1}>
            <dl className="grid grid-cols-[repeat(auto-fill,minmax(max(9rem,40%),1fr))] gap-x-8 gap-y-8 border-y border-white/15 py-8">
              {TS_STATS.map((s) => (
                <div
                  key={s.label}
                  className="flex flex-col-reverse justify-end"
                >
                  <dt className="text-white/70 text-sm md:text-base mt-2 leading-snug">
                    {s.label}
                  </dt>
                  <dd className="font-display text-fwd-yellow text-[clamp(2.5rem,5vw,3.5rem)] leading-none">
                    {s.value}
                  </dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </div>

      {/* Recipient replies, as paper quote cards */}
      <ul className="grid gap-6 md:grid-cols-3 mt-20 md:mt-24">
        {TS_QUOTES.map((q, i) => (
          <li key={q.text} className="flex">
            <Reveal delay={0.1 * i} className="flex w-full">
              <figure className="relative w-full rounded-2xl rounded-tl-sm bg-fwd-white text-fwd-ink px-7 pt-14 pb-7 shadow-[0_20px_50px_rgba(0,0,0,0.35)]">
                <span
                  aria-hidden="true"
                  className="absolute top-3 left-6 font-display text-7xl leading-none text-fwd-red select-none"
                >
                  &ldquo;
                </span>
                <blockquote className="font-display text-lg md:text-xl leading-relaxed">
                  <HighlightedQuote
                    text={q.text}
                    highlight={q.highlight}
                    delay={0.15 * i}
                  />
                </blockquote>
              </figure>
            </Reveal>
          </li>
        ))}
      </ul>
      <p className="text-white/50 text-xs mt-8 text-center">{TS_QUOTES_NOTE}</p>
      <p className="text-white/80 text-base md:text-lg mt-10 text-center">
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-white underline decoration-fwd-yellow decoration-2 underline-offset-4 hover:decoration-white transition-colors"
        >
          Book 15 min
        </a>{" "}
        to discuss your school year milestones.
      </p>
    </div>
  </section>
);
